import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent, DxDropDownBoxComponent } from 'devextreme-angular';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import validationEngine from 'devextreme/ui/validation_engine';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, map, of, shareReplay, switchMap } from 'rxjs';
import { LearningPathModel } from 'src/app/models/learning-paths/learning-path.model';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { PersonCreationAndUpdateModel } from 'src/app/models/person/person-creation-and-update.model';
import { PersonFullModel } from 'src/app/models/person/person-full.model';
import { RoleModel } from 'src/app/models/person/role.model';
import { ProfileModel } from 'src/app/models/profile.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { EventMemberService } from 'src/app/services/event-member.service';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import { PersonService } from 'src/app/services/person.service';
import { RoleService } from 'src/app/services/role.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';

@Component({
  selector: 'app-create-profile-modal',
  standalone: false,
  templateUrl: './create-profile-modal.component.html',
  styleUrl: './create-profile-modal.component.scss'
})
export class CreateProfileModalComponent implements OnInit, AfterViewInit {
  @Input() showModal = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  @Input()
  profile!: ProfileModel;

  @Input()
  trainingId!: string;

  @Output() onSubmit = new EventEmitter<PersonFullModel>();
  @Output() onCancel = new EventEmitter();
  
  @ViewChild('form', { static: false }) 
  form!: DxFormComponent;
  @ViewChild(DxDropDownBoxComponent, { static: false })
  locationDropDown!: DxDropDownBoxComponent;
  @ViewChild(DxDropDownBoxComponent, { static: false })
  departmentDropDown!: DxDropDownBoxComponent;

  rolesOptions!: DxSelectBoxTypes.Properties;

  organizationUnits!: OrganisationUnitModel[];
  locations!: OrganisationUnitModel[];
  trainingGroups!: TrainingGroupModel[];
  learningPaths!: LearningPathModel[];
  roles!: RoleModel[];
  
  profileForm: PersonCreationAndUpdateModel = {
    firstName: null,
    lastName: null,
    locationId: null,
    trainingGroupIds: [],
    learningPathIds: [],
    city: null,
    email: null,
    iddNumber: null,
    agentNumber: null,
    phoneBusiness: null,
    phoneMobile: null,
    phonePrivate: null,
    roleId: null,
    culture: 'de',
    timeZone: 'W. Europe Standard Time',
    imageBase64: null,
    emailSignature: null,
    street: null,
    zipCode: null
  };

  constructor(
    private organisationUnitService: OrganisationUnitService,
    private personService: PersonService,
    private trainingGroupService: TrainingGroupService,
    private learningPathService: LearningPathService,
    private roleService: RoleService,
    private toastrService: ToastrService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private eventMemberService: EventMemberService
  ) {
  }

  ngOnInit() {
    const formData = this.loadingService
      .load(
        forkJoin({
          organizationUnits:
            this.organisationUnitService.getAllOrganisationUnits(),
          trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
          learningPaths: this.learningPathService.getAllLearningPaths().pipe(
            map((learningPaths) => learningPaths.map((learningPath) => ({ ...learningPath, selected: false })))
          ),
          roles: this.roleService.getAllRoles(),
        })
      )
      .pipe(shareReplay(1));

    formData.subscribe(
      ({ organizationUnits, trainingGroups, learningPaths, roles }) => {
        this.organizationUnits = organizationUnits;
        this.locations = organizationUnits;
        this.trainingGroups = trainingGroups;
        this.learningPaths = learningPaths;
        this.roles = roles;
        this.roles.forEach((role) => {
          if (role.name == 'Employee') {
            role.name = this.translateService.instant('COMMON.EMPLOYEE');
          }
          if (role.name == 'Broker') {
            role.name = this.translateService.instant('COMMON.BROKER');
          }
        });

        this.setDropdownOptions();
      }
    );
  }

  ngAfterViewInit() {
    this.patchForm(this.profile);
  }

  onFormSubmit(): void {
    const isFormValid = this.form.instance.validate().isValid;
    const isCustomFieldsValid =
      validationEngine.validateGroup('customFields').isValid;

    if (isFormValid && isCustomFieldsValid) {
      const request$ = this.personService.addPerson(this.profileForm).pipe(
        switchMap((createdPerson: PersonFullModel) => {

          if (!this.profile.eventMemberUnknownId) {
            return of(createdPerson);
          }

          return this.eventMemberService.mergeUnknownEventMember({
            eventId: this.trainingId,
            personId: createdPerson.person.id,
            eventMemberUnknownId: this.profile.eventMemberUnknownId,
          }).pipe(
            map(() => createdPerson)
          );
        })
      );

      this.loadingService.load(request$).subscribe({
        next: (person) => {
          this.toastrService.success(
            this.translateService.instant('EMPLOYEE.FORM.EMPLOYEE-CREATED')
          );
          this.onSubmit.emit(person);
          this.toggleShowModal();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409 && err.error === 'duplicate-email') {
            this.toastrService.warning(
              this.translateService.instant(
                'EMPLOYEE.FORM.DUPLICATE-EMAIL-ERROR'
              )
            );
          } else {
            this.toastrService.error(
              this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
            );
          }
        },
      });
    }
  }

  setDropdownOptions() {
    const defaultSelectOptions = {
      showClearButton: true,
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;

    this.rolesOptions = {
      ...defaultSelectOptions,
      dataSource: this.roles,
      placeholder: '',
    };
  }

  onLocationSelected(e: any) {
    if (e.node.key) {
      this.profileForm.locationId = e.node.key;
    }
  }

  onTrainingGroupSelected(e: any) {
    if (e.node.selected) {
      this.profileForm.trainingGroupIds = [
        ...this.profileForm.trainingGroupIds,
        e.node.key,
      ];
    } else {
      this.profileForm.trainingGroupIds =
        this.profileForm.trainingGroupIds.filter((id) => id !== e.node.key);
    }
  }

  onLearningPathSelected(e: any) {
    if (e.node.selected) {
      this.profileForm.learningPathIds = [
        ...this.profileForm.learningPathIds,
        e.node.key,
      ];
    } else {
      this.profileForm.learningPathIds =
        this.profileForm.learningPathIds.filter((id) => id !== e.node.key);
    }
  }

  patchForm(profile: ProfileModel) {
    this.profileForm.firstName = profile.firstName;
    this.profileForm.lastName = profile.lastName;
    this.profileForm.email = profile.email;
  }

  onSubmitClick = () => {
    this.onFormSubmit();
  };

  onCancelClick = () => {
    this.onCancel.emit();
    this.toggleShowModal();
  };

  toggleShowModal() {
    this.showModal = !this.showModal;
    this.showModalChange.emit(this.showModal);
  }
}
